import { matchRegex } from '@mhub/web-validations';
import { isCountryVietnam } from '../constants/countries';
import { isNumeric } from './numbers/isNumber';
import PAYMENT_METHODS, { PAYMENT_METHOD_BANK_LOAN_OPTION } from '../constants/paymentMethods';
import { TRANSLATION_FILES } from '../constants/translation';

// CPI-974: single place for the Vietnam gate so every VN-only hunk reads the same way.
// Everything below returns the neutral value for MY/ID, so callers stay byte-identical there.
export const isVietnamCompany = auth =>
  isCountryVietnam(auth && auth.company && auth.company.countryCode);

// CPI-1347: book-api refuses change-unit (403 change_unit_not_allowed_vn) for a Vietnam
// booking whose code is not a legacy "BK-" code (CPI-974 D12: cancel and reserve again).
// Mirror that rule so the action is not offered. MY/ID always return false (unchanged).
export const vnChangeUnitBlocked = (auth, booking) =>
  isVietnamCompany(auth) && !!booking && String(booking.code || '').indexOf('BK-') !== 0;

// CPI-974 (D5/S1): "HQ or Sales Admin", the only two roles the Vietnam flows are restricted to -
// the same family book-api tests. Do not widen to auth.isBranchLeader, which admits four roles.
export const hasVietnamElevatedAccess = auth => !!auth && !!(
  auth.isHeadquarters ||
  auth.isDeveloperSalesAdmin
);

// CPI-974 (D2/S1): the source-of-finance options. Vietnam additionally gets Bank Loan;
// PAYMENT_METHODS is returned by reference off Vietnam and is never mutated.
export const paymentMethodOpts = auth => (
  isVietnamCompany(auth) ? [...PAYMENT_METHODS, PAYMENT_METHOD_BANK_LOAN_OPTION] : PAYMENT_METHODS
);

// CPI-974 (D): Vietnam calls the booking-fee rail a "Deposit"; unlisted keys fall through.
const VN_DEPOSIT_LABEL_KEYS = {
  'Booking Fee': 'Deposit',
  'Booking Fee Amount': 'Deposit Amount',
  'New Payment': 'New Deposit',
  'Add Payment': 'Add Deposit',
  'Payment Receipt': 'Deposit Receipt',
};

export const depositLabel = (auth, t, key, options) => {
  if (isVietnamCompany(auth) && VN_DEPOSIT_LABEL_KEYS[key]) {
    return t(VN_DEPOSIT_LABEL_KEYS[key], options);
  }
  return t(key, options);
};

// CPI-974 (D3): Vietnam is approval-only, so the shared booking-fee statuses are relabelled.
const VN_DEPOSIT_STATUS_KEYS = {
  pending_verification: 'Pending Approval',
  paid: 'Approved',
};

// null for MY/ID and for statuses without an override, so call sites keep the old expression.
export const vnDepositStatusLabel = (auth, t, status) => {
  if (isVietnamCompany(auth) && VN_DEPOSIT_STATUS_KEYS[status]) {
    return t(VN_DEPOSIT_STATUS_KEYS[status], { ns: TRANSLATION_FILES.constants });
  }
  return null;
};

// CPI-1212: may this user EDIT a booking's Source of Finance? Vietnam only - outside it the first
// line returns true. Inside, a BASE external agent needs the project's own
// allowAgentEditSourceOfFinance (not the older allowAgentChangeBookingPaymentMethod, which is on).
export const canEditSourceOfFinance = (auth, project) => {
  if (!isVietnamCompany(auth)) return true;
  if (!auth || !auth.companyBranch || !auth.companyBranch.isExternal) return true;
  if (auth.isExternalAgentLeader || auth.isExternalAgencyAdmin) return true;
  return !!(project && project.allowAgentEditSourceOfFinance);
};

// CPI-1230 (D2/D3, A106): "agents see less" - an external company branch is the whole agent
// family, the same predicate the server uses. Deliberately not hasVietnamElevatedAccess inverted.
export const isVietnamExternalAgent = auth => (
  isVietnamCompany(auth) && !!(auth.companyBranch && auth.companyBranch.isExternal)
);

// CPI-1230 (Q1): the bulk-edit "records per page" dropdown gains 300 for Vietnam only.
const UNIT_PAGE_LIMITS_VN_EXTRA = { text: '300', value: '300' };
const UNIT_PAGE_LIMIT_VALUES = ['25', '50', '100', '150'];

export const unitPageLimitOpts = (auth, limits) => (
  isVietnamCompany(auth) ? [...limits, UNIT_PAGE_LIMITS_VN_EXTRA] : limits
);

// The query guards reset an unrecognised ?limit= back to 50, so 300 has to be recognised there.
export const isAllowedUnitPageLimit = (auth, limit) => (
  UNIT_PAGE_LIMIT_VALUES.indexOf(limit) !== -1 ||
  (isVietnamCompany(auth) && limit === UNIT_PAGE_LIMITS_VN_EXTRA.value)
);

// CPI-1219: the two free-text fields a Vietnamese unit carries in its own right - the layout
// description the sales team writes on it ("2PN-2WC") and its view ("SE"). Both have a namesake on
// the unit LAYOUT, which is the product owner's "2 fields both sides"; these two are the unit's.
// Every role sees them, so there is no role gate here - only the country one.
export const VN_UNIT_TEXT_FIELDS = ['unitLayoutNew', 'view'];

// The keys the unit form sends. Outside Vietnam it is an empty object, so no key is added and
// saving the rest of the form cannot blank a stored value. Inside it, an empty box is '', never
// undefined, which the API would drop rather than clear.
export const vnUnitTextValues = (unit, isVietnam) => (
  isVietnam
    ? VN_UNIT_TEXT_FIELDS.reduce((acc, key) => (
      Object.assign(acc, { [key]: (unit && unit[key]) || '' })
    ), {})
    : {}
);

// CPI-1230 (D-D6): may this session type VAT % and Land Value on a unit? The same test the unit
// screen applies to those two rows. False outside Vietnam, where neither field exists.
export const canEditVietnamUnitPricing = auth => (
  isVietnamCompany(auth) &&
  !isVietnamExternalAgent(auth) &&
  hasVietnamElevatedAccess(auth) &&
  !!(auth && auth.allowEditUnitPrice)
);

// CPI-1435 (PO): on a Vietnamese sale Created By is the agent - the same person as the CRM
// booking's agent - so it reads "Agent Assigned". `fallback` is the call site's own (already
// translated) label, returned unchanged for MY/ID.
export const createdByLabel = (auth, t, fallback) => (
  isVietnamCompany(auth)
    ? t('Agent Assigned', { ns: TRANSLATION_FILES.screens })
    : fallback
);

// CPI-1230 (D-D5): "Bumi" / "Bumiputera" in Malaysia is "Foreign restricted" in Vietnam. These
// four helpers return the Malaysian key, placeholder and option array unchanged elsewhere.
export const unitQuotaChipLabel = (auth, t) => (
  isVietnamCompany(auth)
    ? t('Foreign restricted', { ns: TRANSLATION_FILES.screens })
    : t('Bumi')
);

export const unitQuotaFilterPlaceholder = (auth, t) => (
  isVietnamCompany(auth)
    ? t('Foreign restricted', { ns: TRANSLATION_FILES.screens })
    : t('Select Bumiputera')
);

// Vietnam's values are a plain Yes / No pair over the same ?isBumi=true|false query parameter.
export const unitQuotaFilterOptions = (auth, t, defaultOptions) => (
  isVietnamCompany(auth)
    ? [
      { text: t('Yes', { ns: TRANSLATION_FILES.constants }), value: 'true' },
      { text: t('No', { ns: TRANSLATION_FILES.constants }), value: 'false' },
    ]
    : defaultOptions
);

// MS-5972 hid the quota filter outside Malaysia; Vietnam gets it back under its own words.
export const showsUnitQuotaFilter = (auth) => {
  const countryCode = auth && auth.company && auth.company.countryCode;
  if (typeof countryCode !== 'string') {
    return false;
  }
  return countryCode.toUpperCase() === 'MY' || isVietnamCompany(auth);
};

// CPI-1209 (PO, 5 Sep, follow-up to CPI-974 D9): the Booking ID column, defined here once because
// both copies of the sales table call it. bookings.code is the reservation's sr_code for Vietnam.
export const showBookingIDColumn = auth => isVietnamCompany(auth);

// CPI-1429: query for the staff picker (/search/staffs/). For a Vietnamese company a caller may
// also send a sale's id; book-api then lists only staff from the branch of that sale's Assigned
// Staff. Since CPI-1435 no picker sends it (Assigned Staff is the internal reviewer again, and the
// Agent Assigned picker lists anyone). Every MY/ID call builds the exact query it built before.
export const staffSearchQuery = ({ auth, searchAll, search, bookingID } = {}) => {
  let query = `limit=3${searchAll ? '' : '&isExternal=false'}&search=${search}`;
  if (bookingID && isVietnamCompany(auth)) {
    query += `&bookingID=${encodeURIComponent(bookingID)}`;
  }
  return query;
};

// CPI-1435: on a Vietnamese booking form Agent Assigned (Created By) is the CRM booking's agent, so
// it is filled from the CRM and never picked by hand. MY/ID keep their editable Created By picker.
export const agentAssignedLocked = auth => isVietnamCompany(auth);

// CPI-1435: /search/staffs/ query that resolves the CRM booking's agent (its Cognito username,
// the opportunity's assigned_to) to the Showroom user and agency. Empty when there is no agent.
export const crmAgentLookupQuery = assignedTo => (
  assignedTo ? `limit=1&awsUsernames=${encodeURIComponent(assignedTo)}` : ''
);

// CPI-1435: whether a /search/staffs/ row can be a sale's agent - the same fields the Created By
// picker requires (handleUserSelect), including the agency. Anything less is "Invalid assigned
// agent", which book-api also refuses on Create.
export const isCompleteSaleAgent = staff => !!staff &&
  staff.id > 0 &&
  staff.affiliateID > 0 &&
  staff.companyID > 0 &&
  staff.companyBranchID > 0 &&
  !!staff.companyBranchShortName &&
  !!staff.name &&
  !!staff.preferredName;

// A booking with no code reads as a dash, the same placeholder the Sales screen already shows.
export const bookingIDCellValue = booking => (booking && booking.code) || '-';

// CPI-1209 (PO, 11 Sep): "for payment excel to hide that option as deposit payment collection
// not required for skyworld." The Sales list's "Payment Excel" export (booking fee payments) is
// not offered to a Vietnamese company; Malaysia/Indonesia keep it.
export const showsPaymentExcelExport = auth => !isVietnamCompany(auth);

// (Loo, 24 Sep): Sales > Manage > "Bulk Edit Custom Fields" is not offered to a Vietnamese
// company. The Sales list's role check (HQ, Admin, Supervisor, Sales Manager, Sales Admin) still
// applies everywhere else; this only takes the item away in Vietnam. Malaysia/Indonesia unchanged.
export const showsBulkEditCustomFields = auth => !isVietnamCompany(auth);

// CPI-1226 (Loo, 7 Sep): should Sales > Manage fetch the Pending Cancellation list once as the
// page loads, purely so the header badge and the "PENDING CANCELLATION" tab badge are right on
// first paint? Until now that count arrived only when the tab was clicked, so a Vietnamese HQ user
// with an outstanding cancellation request saw 0 until they went looking. Vietnam only - Malaysia
// and Indonesia keep the single tab-1 request they make today.
export const vnPreloadsPendingCancellationCount = auth => isVietnamCompany(auth);

// CPI-1272 (PO, 9 Sep): "to hide this menu items from all AGENCY role such as AL, Agent,
// Agent Admin". companyBranch.isExternal is exactly that family and nothing wider -
// src/actions/auth.js derives isExternalAgentLeader, isExternalAgent AND isExternalAgencyAdmin
// from that one flag - so isVietnamExternalAgent covers all three roles in a single predicate.
// Every developer-side role (HQ, Sales Admin, Sales Manager, Supervisor, Staff, Siteplan) keeps
// the tab, and so does every agency outside Vietnam.
export const showsProjectUnitsTab = auth => !isVietnamExternalAgent(auth);

// CPI-1292 (PO, 14 Sep): "disable this button for Agency user role, vietnam only" - the Reserve
// button on each Project Overview > Layouts row. It only opens the layout's unit-list modal, which
// knows nothing about agency allocation, so a VN agent could pick a unit their branch is not
// allocated and dead-end on book-api's 403. Hidden for the same agency family as the Units tab
// above; the unit page's own Reserve (allocation-checked server-side, CPI-975) stays their path.
export const showsLayoutReserveButton = auth => !isVietnamExternalAgent(auth);

// the "Unit Availability" block at the bottom of a Layout's Overview page is the
// same unit list the Units tab (CPI-1272) already keeps from Vietnamese agency roles, just filtered
// to one layout. Hidden for the same family so an agent cannot read the list through the layout
// page instead; the segment is not drawn at all, so the units request is never made either.
export const showsLayoutUnitAvailability = auth => !isVietnamExternalAgent(auth);

// CPI-1372 (PO, 29 Sep): "hide selling prices from Agency users via project setting". The
// project's existing "Disable Base Selling Price in pricing package selection" box takes on a
// second meaning in Vietnam: ticked, an agency user (AL, Agent, Agent Admin - the same family as
// above) is shown no price at all on Unit Information - list price, before/after-tax selling
// price, and the pricing-package / fee / mortgage segments, which already key off spaValue.
// book-api zeroes the same fields in the payload (handlers/vn_gates.go vnStripUnitPricesForAgency),
// so this only decides what the screen draws; the rows are removed, never shown as "-".
// Developer-side roles, every company outside Vietnam, and a project with the box clear all
// return false and keep the screen exactly as it is.
export const hidesPricesFromAgency = (auth, project) => (
  isVietnamExternalAgent(auth) && !!(project && project.disableBaseSellingPriceSelection)
);

// CPI-1274 (PO, 9 Sep): "to hide storey plan from Agent if project is not published. the project
// not published setting is in project > edit > info screen." That screen's first field is the
// Status select over PROJECT_STATUSES (src/constants/projectStatuses.js), so "published" here is
// projects.status_id - NOT the Marketplace tab's marketplaceIsPublish / marketplaceBuyerIsPublish
// toggles, which live on a different tab and gate a different product.
//
// The project's own salesChartEnabled switch still decides whether a Storey Plan link exists at
// all; this only takes it away again from a Vietnamese agency while the project is draft, ready,
// archived or deleted. Developer-side roles, and everyone outside Vietnam, are unaffected.
export const showsProjectStoreyPlan = (auth, project) => (
  !isVietnamExternalAgent(auth) || !!(project && project.statusID === 'published')
);

// CPI-974: a Vietnamese CCCD is 12 digits, not a Malaysian IC. Reported twice - by Sales on
// Skyworld staging (8 Sep, 912345678123 on a new booking) and by the PO on CPI-977 comment
// 161432 item 3 (001198012345). @mhub/web-validations only knows the MY and ID formats, so
// identity type 1 has to carry its own regex here. The Purchaser tab, the Customer screen and
// the Sales booking form all read it from this one place.
//
// The rule is the CRM's Vietnam config rule, aligned to the CRM's VN config, Max 8 Sep:
// mhub-leads-v2-web src/localization/features/constants.js, COUNTRIES.vn.ic_validation_regex =
// /^[0-9]{12}$/ - exactly 12 digits and nothing else. This replaces the structured pattern
// CPI-149 wrote in March (/^(?:0\d{2}|[1-9]\d{2})[0-3]\d{8}$/, which also constrained the 4th
// digit to 0-3) on all three screens. The SRB buyer app checks 12 digits too, so the CRM, SRB
// and the Showroom now agree on what a CCCD is.
export const VN_CCCD_REGEX = /^[0-9]{12}$/;

// The CRM's own wording for a malformed CCCD (validation.cccd_format in its en / vi-VN key
// files), carried into the Showroom so a Vietnamese user reads the same sentence in both
// products instead of the library's "<label> has an invalid identity type".
export const VN_CCCD_MESSAGE = 'CCCD must be exactly 12 digits, containing only numbers without spaces or punctuation.';

// null everywhere else - outside Vietnam, and for every identity type but CCCD - so a caller
// keeps falling through to the countryCode/identityType option it passes today, message and all.
// @mhub/web-validations returns options.message verbatim when it is set (dist/lib/getMessage.js).
export const vnIdentityValidation = (auth, identityTypeID, t) => {
  if (!isVietnamCompany(auth) || identityTypeID !== 1) {
    return null;
  }
  return {
    validRegex: VN_CCCD_REGEX,
    message: typeof t === 'function'
      ? t(VN_CCCD_MESSAGE, { ns: TRANSLATION_FILES.translation })
      : VN_CCCD_MESSAGE,
  };
};

// CPI-1319: a Vietnamese purchaser's Full Name and Preferred Name (and a company's Contact
// Person) must not contain digits. Only digits are refused, so Vietnamese letters with diacritics
// pass untouched. The Company Name of an Enterprise Registration Certificate purchaser (identity
// type 4) may carry numbers, so the name field skips the rule there. Outside Vietnam this returns
// [] and the callers' validation arrays stay byte-identical.
export const VN_NAME_NO_DIGITS_REGEX = /^[^0-9]*$/;
export const VN_NAME_FORMAT_MESSAGE = 'cannot contain digits';
export const IDENTITY_TYPE_VN_ENTERPRISE = 4;

// "Preferred Name cannot contain digits" - the label leads, like "<Field> is required", so the
// "Please fix form errors" popup, which lists messages only, says which field failed.
export const vnNameFormatMessage = (t, label) => {
  const text = typeof t === 'function'
    ? t(VN_NAME_FORMAT_MESSAGE, { ns: TRANSLATION_FILES.translation })
    : VN_NAME_FORMAT_MESSAGE;
  return label ? `${label} ${text}` : text;
};

export const vnNameValidations = (auth, t, options = {}) => {
  if (!isVietnamCompany(auth)) return [];
  if (options.isCompanyName) return [];
  return [{
    validate: matchRegex,
    option: {
      validRegex: VN_NAME_NO_DIGITS_REGEX,
      // matchRegex calls a function message with { key, label, validRegex }; label is the
      // input's validateLabel, already translated by the screen.
      message: ({ label }) => vnNameFormatMessage(t, label),
    },
  }];
};

// Sugar for the two name inputs: the Full Name / Company Name box and the Preferred Name /
// Contact Person box. identityTypeID is the purchaser's selected identity type.
export const vnFullNameValidations = (auth, identityTypeID, t) => vnNameValidations(auth, t, {
  isCompanyName: Number(identityTypeID) === IDENTITY_TYPE_VN_ENTERPRISE,
});
export const vnPreferredNameValidations = (auth, t) => vnNameValidations(auth, t);

// CPI-1291: the CRM sends monthly_income as a string and BookingCustomer.monthlyIncome is a
// float64. Only a whole plain number is taken (parseFloat half-reads "1.200.000" as 1.2 and
// "1,200" as 1); blank and unreadable alike become null, which Go leaves at the zero value.
export const crmMonthlyIncome = value => (
  (typeof value === 'string' || typeof value === 'number') && isNumeric(value) ? Number(value) : null
);

// CPI-977: the CRM keeps contact.mobile as the national number ("889321232") and the full number
// in msisdn ("84889321232"), but some contacts carry the full number in mobile as well. Posted
// untouched, book-api's phone preset regex for the prefix rejects it ("Invalid Mobile Number").
// The primary purchaser at least sees it in the Mobile input; a related purchaser is posted
// straight from the CRM payload and fails the reservation with nothing to correct on screen.
// Drop "+", spaces and separators, then the dialling code when the number leads with it and is
// too long to be national. A national number can start with the code's own digits (VN 084 is
// "84xxxxxxx" without the trunk zero), so the code only goes when at least
// CRM_MOBILE_MIN_NATIONAL_LENGTH digits remain - the shortest mobile numbering plans are 8.
export const CRM_MOBILE_MIN_NATIONAL_LENGTH = 8;
export const crmContactMobile = (mobile, mobilePrefix) => {
  const digits = String(mobile || '').replace(/\D/g, '');
  const prefix = String(mobilePrefix || '').replace(/\D/g, '');
  if (!prefix || !digits.startsWith(prefix)) return digits;
  const national = digits.slice(prefix.length);
  return national.length >= CRM_MOBILE_MIN_NATIONAL_LENGTH ? national : digits;
};

// CPI-1248 (part 1): a Vietnamese unit flagged `isBumi` is the "Local unit" - the same column the
// unit screens label "Foreign restricted" (`unitQuotaChipLabel` above) - and no foreign purchaser
// may reserve one. Everything below is Vietnam-only at the call site; MY/ID never reach it.
//
// Citizenship is a declared 'local' | 'foreigner' and nothing else. It is NEVER inferred from a
// passport, a nationality, an address country or the buyer's identity type: a Vietnamese citizen
// living abroad and a foreigner holding a CCCD both exist, so only the declaration is evidence.
// Blank means "not declared", which does not block - the API applies the same rule on the write.
export const CITIZENSHIP_LOCAL = 'local';
export const CITIZENSHIP_FOREIGNER = 'foreigner';

// The sentence the user reads, kept verbatim here so the screen, the flash on the API's own 412
// and the tests cannot drift apart. English is the VN UI convention; a missing key falls back to
// exactly this text.
export const VN_LOCAL_UNIT_FOREIGNER_MESSAGE = 'Foreigners are not allowed to reserve Local unit';

// CPI-1248 (part 2): the project's Foreign Purchase Control threshold (CPI-1220). book-api counts
// the units already held by a foreigner at reservation time and answers 412 /
// `M-Type: error_vn_foreign_threshold_exceeded`; the frontend counts nothing and only renders the
// refusal, so this sentence exists here purely as the fallback when the API sends no body.
export const VN_FOREIGN_THRESHOLD_MESSAGE = 'Exceed foreign control threshold, please contact your agent for assistance.';

// '' for anything that is not one of the two declared values, including null/undefined, a number,
// an object, or free text that merely contains the word ("Local ward 3" is an address, not a
// citizenship).
export const normalizeCitizenship = (value) => {
  if (typeof value !== 'string') return '';
  const normalized = value.trim().toLowerCase();
  return normalized === CITIZENSHIP_LOCAL || normalized === CITIZENSHIP_FOREIGNER ? normalized : '';
};

// The CRM holds citizenship in two places. The newer customer API has its own `citizenship`;
// before it, the CRM's Register Customer screen wrote the same two values into `address_4`
// (mhub-leads-v2-web RegisterCustomer.jsx `handleChangeCitizenship`). The explicit field wins,
// and the legacy one is read ONLY when it holds one of the two values - on a customer registered
// before that screen existed, `address_4` is an ordinary address line (a ward, a street) and
// says nothing about citizenship.
export const crmContactCitizenship = (contact) => {
  if (!contact) return '';
  return normalizeCitizenship(contact.citizenship) || normalizeCitizenship(contact.address_4);
};

// A purchaser the booking form holds: its citizenship has already been resolved by the mapping
// above, so only the explicit field is read here.
export const isForeignPurchaser = purchaser => (
  !!purchaser && normalizeCitizenship(purchaser.citizenship) === CITIZENSHIP_FOREIGNER
);

// Primary OR any joint purchaser. The rule is binary: a mixed set (a local primary with a foreign
// joint purchaser, or the reverse) counts as foreign, because the foreigner is still on the unit.
export const hasForeignPurchaser = purchasers => (
  Array.isArray(purchasers) && purchasers.some(isForeignPurchaser)
);

// `unit` is the selected unit's own details (`projectUnitDetails` on the booking form). False for
// every non-Vietnamese session, so MY/ID keep reserving exactly what they reserved before.
export const isVietnamLocalUnit = (auth, unit) => (
  isVietnamCompany(auth) && !!(unit && unit.isBumi)
);

// The one predicate the booking form asks before it writes: may this set of purchasers reserve
// this unit? True means block.
export const blocksVietnamLocalUnit = (auth, unit, purchasers) => (
  isVietnamLocalUnit(auth, unit) && hasForeignPurchaser(purchasers)
);

// CPI-1332 item 1 — a purchaser's identity document, by country and type.
// Vietnam: a CCCD has a front and a back; a passport or an Enterprise Registration Certificate is
// one image (kept in the front slot). Everywhere else the card is what it always was: two sides.
export const idDocumentSides = (identityTypeID, isVietnam) => (
  isVietnam && identityTypeID !== 1 ? 1 : 2
);

// Whether the document on file is complete for its type. Keeps the screen's existing `!== ''`
// reading of the ids, so MY/ID stay byte-identical; only a one-image Vietnamese document stops
// waiting for a back it does not have.
export const idDocumentComplete = (customer, isVietnam) => (
  !!customer &&
  customer.icFrontDocumentID !== '' &&
  (idDocumentSides(customer.identityTypeID, isVietnam) === 1 || customer.icBackDocumentID !== '')
);

// The upload entries to send for a document: the back is dropped for a one-image type, so a file
// picked while CCCD was selected cannot ride along after the agent switches to a passport.
export const idDocumentUploads = (front, back, sides) => {
  const uploads = [];
  if (front && front.file) uploads.push({ index: 0, upload: front });
  if (back && back.file && sides === 2) uploads.push({ index: 1, upload: back });
  return uploads;
};

// The CRM stores a Vietnamese identity in cccd / passport / other_identity, never in nric, so a
// search by the Malaysian field name finds nobody. Same mapping book-api uses when it writes the
// contact (FromShowroomCustomer). Non-Vietnam searches are passed through unchanged.
export const vnCRMSearchField = (searchType, isVietnam) => {
  if (!isVietnam) return searchType;
  if (searchType === 'nric') return 'cccd';
  if (searchType === 'company') return 'other_identity';
  return searchType;
};



// WEBPACK FOOTER //
// ./src/utils/vietnam.js